import React, { FC, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Divider,
} from '@mui/material';
import { Category, SubCategory } from 'models/organization';
import { useTranslation } from 'react-i18next';

type ActionFilterProps = {
    categories: Category[];
    onFilterChange: (type: string, value: string) => void;
    onAddAction: () => void;
};

const ActionFilter: FC<ActionFilterProps> = ({ categories, onFilterChange, onAddAction }) => {
    const { t: translate } = useTranslation(['client-directory', 'common', 'orders']);
    const [searchByMainCategory, setSearchByMainCategory] = useState('');
    const [searchBySubCategory, setSearchBySubCategory] = useState('');
    const [searchByNameOrNumber, setSearchByNameOrNumber] = useState('');
    const [filteredSubCategories, setFilteredSubCategories] = useState<SubCategory[]>([]);
    const getSubCategories = (categoryFilter: string, id: string) => {
        setSearchByMainCategory(id);
        if (id) {
            const category = categories.find((cat) => cat._id === id);
            setFilteredSubCategories(category?.subCategories || []);
            setSearchBySubCategory('');
        } else {
            setFilteredSubCategories([]);
            setSearchBySubCategory('');
        }

        if (categoryFilter) {
            setSearchBySubCategory('');
            onFilterChange('category', id);
        }
    };

    const handleSubCategoryChange = (value: string) => {
        setSearchBySubCategory(value);
        onFilterChange('subCategory', value);
    };

    const handleNameOrNumberChange = (value: string) => {
        setSearchByNameOrNumber(value);
        onFilterChange('action', value);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('orders:ACTIONS')}
            </Typography>

            <Divider />

            <Grid mt={1} container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                        <InputLabel>{translate('client-directory:MAIN_CATEGORY')}</InputLabel>
                        <Select
                            value={searchByMainCategory}
                            onChange={(e) => getSubCategories('category_filter', e.target.value)}
                            label={translate('client-directory:MAIN_CATEGORY')}
                        >
                            <MenuItem value="">{translate('common:ALL')}</MenuItem>
                            {categories?.map((category) => (
                                <MenuItem key={category._id} value={category._id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth disabled={!searchByMainCategory || filteredSubCategories.length === 0}>
                        <InputLabel>{translate('client-directory:SUB_CATEGORY')}</InputLabel>
                        <Select
                            value={searchBySubCategory}
                            onChange={(e) => handleSubCategoryChange(e.target.value)}
                            label={translate('client-directory:SUB_CATEGORY')}
                        >
                            {!searchByMainCategory && (
                                <MenuItem value="" disabled>
                                    {translate('common:ALL')}
                                </MenuItem>
                            )}
                            {filteredSubCategories.map((subCategory) => (
                                <MenuItem key={subCategory._id} value={subCategory._id}>
                                    {subCategory.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        type="search"
                        placeholder={translate('orders:SEARCH_FOR_A_ACTION_EITHER_BY_NAME_OR_NUMBER')}
                        value={searchByNameOrNumber}
                        onChange={(e) => handleNameOrNumberChange(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Button onClick={() => onAddAction()}>+ {translate('common:ADD')}</Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ActionFilter;
